@if (notFound()) {
  <section class="sav-cc-missing">
    <div class="sav-site-container">
      <h1>Coupons not found</h1>
      <p>This coupon collection isn't available. Browse all stores to find deals instead.</p>
      <a class="sav-cc-btn is-primary" routerLink="/storelist">Browse all stores</a>
    </div>
  </section>
} @else if (collection(); as def) {
  <section class="sav-cc-hero">
    <div class="sav-site-container">
      <div class="sav-cc-hero-inner">
        <app-breadcrumb [items]="crumbs()" />

        @if (total() > 0) {
          <span class="sav-cc-pill">
            <svg
              class="sav-cc-pill-icon"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <path
                d="M12 2.5l2.1 5.4 5.4 2.1-5.4 2.1L12 17.5l-2.1-5.4L4.5 10l5.4-2.1L12 2.5z"
                fill="currentColor"
              />
            </svg>
            {{ total() }} {{ def.pillLabel }} live today
          </span>
        } @else {
          <span class="sav-cc-pill">{{ def.eyebrow }}</span>
        }

        <h1 class="sav-cc-title">{{ def.heroHeading }}</h1>
        <p class="sav-cc-subtitle">{{ def.description }}</p>

        <form class="sav-cc-search" (submit)="onSearch($event, storeQuery.value)">
          <span class="sav-cc-search-field">
            <svg
              class="sav-cc-search-icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              aria-hidden="true"
            >
              <path
                d="M10.5 3a7.5 7.5 0 015.916 12.13l4.227 4.226a1 1 0 01-1.414 1.415l-4.227-4.227A7.5 7.5 0 1110.5 3zm0 2a5.5 5.5 0 100 11 5.5 5.5 0 000-11z"
                fill="currentColor"
              />
            </svg>
            <input
              #storeQuery
              type="search"
              class="sav-cc-search-input"
              placeholder="Search a store, e.g. Nykaa"
              aria-label="Search a store"
              autocomplete="off"
            />
          </span>
          <button type="submit" class="sav-cc-search-btn">Find deals</button>
        </form>
      </div>
    </div>
  </section>

  <section class="sav-cc-list-section" aria-label="Coupons">
    <div class="sav-site-container">
      @if (coupons().length) {
        <app-section-heading
          [heading]="def.title"
          [subtitle]="
            total() > 0
              ? total() + ' ' + def.pillLabel + ' checked and live right now'
              : 'Checked and live right now'
          "
        />

        <!--
          The home page's Trending Coupons card, reused class-for-class rather than restyled: the
          layout, type and both grid breakpoints come from the global `.sav-trending-coupon-*` rules
          in styles.scss, so these cards are the same width and height as the home page's by
          construction and cannot drift from them.

          The admin badge pills the old card rendered are dropped deliberately — the only badge in
          the data is "Verified", which this card already shows in its top row.
        -->
        <div class="sav-trending-coupons-grid">
          @for (coupon of coupons(); track coupon.id) {
            <article class="sav-trending-coupon-card">
              <a
                class="sav-trending-coupon-brand"
                [routerLink]="coupon.storeSlug ? '/' + coupon.storeSlug : null"
                [attr.aria-label]="coupon.storeName"
              >
                <!-- `.sav-trending-coupon-brand-logo` stays on the slot: that global rule sizes it
                     (92px) and draws the ring. Decorative because the link's aria-label names the
                     store. The path is only resolved when there is one, so an empty path never
                     turns into a request for the asset origin itself. -->
                <app-store-logo
                  class="sav-trending-coupon-brand-logo"
                  [logoUrl]="coupon.storeLogoUrl ? (coupon.storeLogoUrl | assetUrl) : ''"
                  [name]="coupon.storeName"
                  decorative
                />
              </a>

              <div class="sav-trending-coupon-content">
                <div class="sav-trending-coupon-top-row">
                  <span class="sav-trending-coupon-type">{{
                    coupon.isDeal ? 'Deal' : 'Code'
                  }}</span>
                  <span class="sav-trending-coupon-verified">Verified</span>
                </div>

                <a
                  class="sav-trending-coupon-store"
                  [routerLink]="coupon.storeSlug ? '/' + coupon.storeSlug : null"
                  >{{ coupon.storeName }}</a
                >

                <!-- `.type-inherit` so the name takes the card's body type rather than the h3
                     scale, matching the home page's trending coupon card. -->
                <h3 class="type-inherit">{{ coupon.title }}</h3>

                <div class="sav-trending-coupon-bottom-row">
                  <span>Expires : {{ coupon.expiresLabel }}</span>

                  @if (coupon.isDeal) {
                    <a [routerLink]="coupon.storeSlug ? '/' + coupon.storeSlug : null" class="deal"
                      >Get Deal</a
                    >
                  } @else {
                    <!-- A button, not a link: revealing the code is an in-page action. It picks up
                         the same footer-link styling from the shared rule in styles.scss. -->
                    <button type="button" (click)="toggleCode(coupon)">
                      @if (isRevealed(coupon.id)) {
                        <span class="sav-trending-coupon-code-suffix">{{ coupon.code }}</span>
                      } @else {
                        <!-- Nothing of the code before the click — only the full code once
                             revealed, which is what the click is for. -->
                        <span class="sav-trending-coupon-code-label">Reveal Code</span>
                      }
                    </button>
                  }
                </div>
              </div>
            </article>
          }
        </div>

        @if (hasMore()) {
          <div class="sav-cc-load-more-wrap">
            <button
              type="button"
              class="sav-cc-load-more"
              [disabled]="loadingMore()"
              (click)="loadMore()"
            >
              @if (loadingMore()) {
                Loading…
              } @else {
                Load more ({{ coupons().length }} of {{ total() }})
              }
            </button>
          </div>
        }
      } @else if (isLoading()) {
        <p class="sav-cc-empty">Loading coupons…</p>
      } @else {
        <div class="sav-cc-empty-state">
          <p>No coupons in this collection right now. Check back soon.</p>
          <a class="sav-cc-btn is-primary" routerLink="/storelist">Browse all stores</a>
        </div>
      }
    </div>
  </section>

  @if (stores().length) {
    <section class="sav-cc-stores-section" aria-label="Stores in this collection">
      <div class="sav-site-container">
        <app-section-heading
          [heading]="'Stores with ' + def.badge + ' coupons'"
          subtitle="Jump straight to a store's own page for its full offer list."
        />
        <div class="sav-cc-stores-grid">
          @for (store of stores(); track store.slug) {
            <a class="sav-cc-store-card" [routerLink]="store.slug ? '/' + store.slug : null">
              <span class="sav-cc-store-card-logo">
                <!-- Square: this slot never rounded its logos, so the component must not either. -->
                <app-store-logo
                  [logoUrl]="store.logoUrl ? (store.logoUrl | assetUrl) : ''"
                  [name]="store.name"
                  shape="square"
                  decorative
                />
              </span>
              <span class="sav-cc-store-card-body">
                <span class="sav-cc-store-card-name">{{ store.name }}</span>
                <span class="sav-cc-store-card-count">{{ store.countLabel }}</span>
              </span>
            </a>
          }
        </div>
      </div>
    </section>
  }

  @if (def.longContent) {
    <section class="sav-cc-content-section" aria-label="About this collection">
      <div class="sav-site-container">
        <div class="sav-cc-content" [innerHTML]="def.longContent | trustedCmsHtml"></div>
      </div>
    </section>
  }

  @if (def.faqs.length) {
    <section class="sav-cc-faq-section" aria-label="Frequently asked questions">
      <div class="sav-site-container">
        <app-section-heading
          heading="Frequently Asked Questions"
          [subtitle]="'What shoppers ask about ' + def.title.toLowerCase() + '.'"
        />
        <!-- Shared FAQ accordion — see `.sav-faq-*` in styles.scss. The chevron SVG this used is
             gone; the shared design draws a "+" that rotates when the row opens. -->
        <div class="sav-faq-list">
          @for (faq of def.faqs; track faq.question; let i = $index) {
            <div class="sav-faq-item" [class.expanded]="expandedFaq() === i">
              <button
                type="button"
                class="sav-faq-question"
                (click)="toggleFaq(i)"
                [attr.aria-expanded]="expandedFaq() === i"
              >
                {{ faq.question }}
              </button>
              <div class="sav-faq-answer" [class.visible]="expandedFaq() === i">
                <div class="sav-faq-answer-body">{{ faq.answer }}</div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>
  }
}
